import * as RadixSelect from '@radix-ui/react-select';
import { forwardRef, useId, useRef, useState, type ComponentProps } from 'react';
import { Input as InputComponent } from '~/components/ui/Input';
import { classNames } from '~/utils/classNames';
import { forwardRefMemo } from '~/utils/react';
import { scrollToElementWithOffset } from '~/utils/scroll';

export const Root = forwardRef<
  HTMLFormElement,
  {
    className?: string;
    children: React.ReactNode;
  }
>((props, forwardedRef) => {
  function preventSubmit(event: React.FormEvent) {
    event.preventDefault();
  }

  return (
    <form
      onSubmit={preventSubmit}
      className={classNames('gap-5 grid p-6', props.className)}
      ref={forwardedRef}
      {...props}
      autoComplete="off"
      autoCorrect="off"
    >
      {props.children}
    </form>
  );
});

export const Input = forwardRefMemo<HTMLInputElement, ComponentProps<typeof InputComponent>>((props, forwardedRef) => {
  return (
    <InputComponent
      {...props}
      size="sm"
      ref={forwardedRef}
      onFocus={(event) => scrollToElementWithOffset(event.target.parentElement?.parentElement?.parentElement, 24)}
    />
  );
});

export const Textarea = forwardRefMemo<
  HTMLTextAreaElement,
  {
    label: string;
    name: string;
    defaultValue?: string;
    placeholder?: string;
    error?: string;
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  }
>((props, forwardedRef) => {
  const id = useId();

  const resizerRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    props.onChange?.(e);

    if (resizerRef.current) {
      resizerRef.current.textContent = `${e.target.value} `;
    }
  }

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label htmlFor={id} className="text-bolt-ds-textSecondary font-medium text-xs">
        {props.label}
      </label>

      <div className="flex grow-1 flex-col gap-1.5">
        <div
          className={classNames(
            `
          text-bolt-ds-textPrimary grid p-3 box-content text-sm leading-normal
            border border-bolt-ds-borderOutline rounded
            bg-bolt-ds-surfaceOne
            focus-within:ring-2 focus-within:ring-bolt-ds-brandHighlight
          `,
          )}
        >
          <textarea
            id={id}
            ref={forwardedRef}
            name={props.name}
            defaultValue={props.defaultValue ?? ''}
            onChange={handleChange}
            placeholder={props.placeholder}
            onFocus={() => scrollToElementWithOffset(containerRef.current, 24)}
            className="resize-none [grid-area:1/1/2/2] outline-unset bg-transparent placeholder:text-bolt-ds-textTertiary"
          />
          <div
            ref={resizerRef}
            className="outline-0 invisible line-clamp-10 whitespace-pre-wrap [grid-area:1/1/2/2]"
          >{`${props.defaultValue} `}</div>
        </div>

        {props.error && <p className="text-bolt-ds-danger text-xs">{props.error}</p>}
      </div>
    </div>
  );
});

type SelectOption =
  | { value: string; label: string; icon?: string }
  | { type: 'separator'; id: string }
  | { type: 'action'; label: string; icon?: string; onClick: () => void };

export const Select = forwardRefMemo<
  HTMLButtonElement,
  {
    defaultValue?: string;
    value?: string;
    name: string;
    label: string;
    placeholder?: string;
    options: SelectOption[];
    error?: string;
    onValueChange?: (value: string) => void;
  }
>((props, forwardedRef) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <RadixSelect.Root
      defaultValue={props.defaultValue}
      value={props.value}
      name={props.name}
      onValueChange={props.onValueChange}
      onOpenChange={setOpen}
      open={open}
    >
      <div className="flex flex-col gap-1.5" ref={containerRef}>
        <label htmlFor={id} className="text-bolt-ds-textSecondary font-medium text-xs">
          {props.label}
        </label>

        <RadixSelect.Trigger
          ref={forwardedRef}
          id={id}
          onFocus={() => scrollToElementWithOffset(containerRef.current, 24)}
          className="flex items-center data-[placeholder]:text-bolt-elements-textSecondary justify-between border border-bolt-elements-borderColor focus:ring-2 focus:ring-bolt-ds-brandHighlight text-sm gap-2 h-10 bg-bolt-elements-background-depth-2 px-4 rounded-lg focus:outline-none"
        >
          <RadixSelect.Value placeholder={props.placeholder} />
          <RadixSelect.Icon asChild>
            <span className="i-ph:caret-down inline-block text-bolt-elements-icn" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        {props.error && <p className="text-bolt-ds-danger text-xs">{props.error}</p>}
      </div>

      <RadixSelect.Portal>
        <RadixSelect.Content
          onEscapeKeyDown={(event) => {
            event.stopPropagation();
            setOpen(false);
          }}
          className="bg-bolt-elements-background-depth-2 border border-bolt-elements-borderColor rounded-lg shadow z-dialog"
        >
          <RadixSelect.Viewport className="p-1">
            <RadixSelect.SelectGroup>
              {props.options.map((option) =>
                'type' in option && option.type === 'separator' ? (
                  <RadixSelect.Separator key={option.id} className="border-bolt-elements-borderColor border-t my-1" />
                ) : 'value' in option ? (
                  <RadixSelect.Item
                    key={option.value}
                    value={option.value}
                    className="relative flex items-center px-8 h-9 text-sm cursor-pointer [&[data-highlighted]]:text-bolt-elements-item-contentActive [&[data-highlighted]]:bg-bolt-elements-item-backgroundActive text-bolt-elements-textSecondary rounded outline-none"
                  >
                    <RadixSelect.ItemText>
                      <span>{option.label}</span>
                    </RadixSelect.ItemText>
                    <RadixSelect.ItemIndicator className="absolute left-2 inline-flex items-center">
                      <span className="i-ph:check" />
                    </RadixSelect.ItemIndicator>
                  </RadixSelect.Item>
                ) : 'type' in option && option.type === 'action' ? (
                  <RadixSelect.Item
                    value="undefined as unknown as string"
                    key={option.label}
                    onClick={() => {
                      option.onClick();
                      setOpen(false);
                    }}
                    onPointerDown={(event) => event.preventDefault()}
                    onPointerUp={(event) => event.preventDefault()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        option.onClick();
                        setOpen(false);
                      }
                    }}
                    className="relative flex items-center px-8 h-9 text-sm cursor-pointer [&[data-highlighted]]:text-bolt-elements-item-contentActive [&[data-highlighted]]:bg-bolt-elements-item-backgroundActive text-bolt-elements-textSecondary rounded outline-none"
                  >
                    {option.icon && (
                      <span
                        aria-hidden="true"
                        className={classNames(option.icon, 'absolute left-2 inline-flex items-center')}
                      />
                    )}
                    <RadixSelect.ItemText>
                      <span>{option.label}</span>
                    </RadixSelect.ItemText>
                  </RadixSelect.Item>
                ) : null,
              )}
            </RadixSelect.SelectGroup>
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
});
